export type AdminCaptureHttpMethod =
  | 'GET'
  | 'PATCH'
  | 'POST'
  | 'PUT'
  | 'DELETE';

export type AdminRequestCaptureContext = {
  endpoint: string;
  httpMethod: AdminCaptureHttpMethod;
  requestUrl: string;
  orderId?: string;
  productId?: string;
  itemId?: string;
  bannerId?: string;
};

type AdminCaptureScope = {
  setTag: (key: string, value: string) => unknown;
  setContext: (name: string, context: Record<string, unknown>) => unknown;
};

const UUID_PATTERN =
  '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';

const ORDER_ITEM_PATH = new RegExp(
  `backoffice/order/(${UUID_PATTERN})/item/(${UUID_PATTERN})`
);
const ORDER_SUBMIT_PATH = new RegExp(
  `backoffice/order/submit/(${UUID_PATTERN})`
);
const ORDER_PATH = new RegExp(`backoffice/order/(${UUID_PATTERN})`);
const PRODUCT_PATH = new RegExp(`backoffice/product/(${UUID_PATTERN})`);
const BANNER_PATH = new RegExp(`backoffice/banner/(${UUID_PATTERN})`);

function stripQuery(path: string): string {
  const queryIndex = path.indexOf('?');
  if (queryIndex === -1) {
    return path;
  }
  return path.slice(0, queryIndex);
}

function buildQueryString(
  queryParams?: Record<string, unknown>
): string | undefined {
  if (!queryParams) {
    return undefined;
  }

  const searchParams = new URLSearchParams();
  Object.entries(queryParams).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (entry !== undefined && entry !== null) {
          searchParams.append(key, String(entry));
        }
      });
      return;
    }
    searchParams.append(key, String(value));
  });

  const serialized = searchParams.toString();
  return serialized.length > 0 ? serialized : undefined;
}

/**
 * Builds a safe admin Sentry request context from a path.
 * Domain IDs are best-effort and omitted when the path does not match known patterns.
 */
export function parseAdminRequestCaptureContext(
  requestPath: string,
  httpMethod: AdminCaptureHttpMethod,
  queryParams?: Record<string, unknown>
): AdminRequestCaptureContext {
  const pathWithQuery = requestPath.trim();
  const endpoint = stripQuery(pathWithQuery);
  const queryString = buildQueryString(queryParams);
  const requestUrl =
    queryString && !pathWithQuery.includes('?')
      ? `${endpoint}?${queryString}`
      : pathWithQuery;

  const context: AdminRequestCaptureContext = {
    endpoint,
    httpMethod,
    requestUrl,
  };

  const orderItemMatch = endpoint.match(ORDER_ITEM_PATH);
  if (orderItemMatch) {
    context.orderId = orderItemMatch[1];
    context.itemId = orderItemMatch[2];
    return context;
  }

  const orderSubmitMatch = endpoint.match(ORDER_SUBMIT_PATH);
  if (orderSubmitMatch) {
    context.orderId = orderSubmitMatch[1];
    return context;
  }

  const orderMatch = endpoint.match(ORDER_PATH);
  if (orderMatch) {
    context.orderId = orderMatch[1];
    return context;
  }

  const productMatch = endpoint.match(PRODUCT_PATH);
  if (productMatch) {
    context.productId = productMatch[1];
    return context;
  }

  const bannerMatch = endpoint.match(BANNER_PATH);
  if (bannerMatch) {
    context.bannerId = bannerMatch[1];
  }

  return context;
}

export type EnrichAdminCaptureScopeOptions = {
  requestPath: string;
  httpMethod: AdminCaptureHttpMethod;
  queryKey: string;
  operation: string;
  statusCode?: number | string;
  errorBody?: unknown;
  queryParams?: Record<string, unknown>;
};

/**
 * Applies baseline admin capture tags/context, plus optional domain ID tags when parseable.
 */
export function enrichAdminCaptureScope(
  scope: AdminCaptureScope,
  options: EnrichAdminCaptureScopeOptions
): void {
  const parsed = parseAdminRequestCaptureContext(
    options.requestPath,
    options.httpMethod,
    options.queryParams
  );

  scope.setTag('endpoint', parsed.endpoint);
  scope.setTag('http_method', parsed.httpMethod);
  scope.setTag('operation', options.operation);
  scope.setTag('queryKey', options.queryKey);

  if (options.statusCode !== undefined && options.statusCode !== null) {
    scope.setTag('status_code', String(options.statusCode));
  }
  if (parsed.orderId) {
    scope.setTag('orderId', parsed.orderId);
  }
  if (parsed.productId) {
    scope.setTag('productId', parsed.productId);
  }
  if (parsed.itemId) {
    scope.setTag('itemId', parsed.itemId);
  }
  if (parsed.bannerId) {
    scope.setTag('bannerId', parsed.bannerId);
  }

  scope.setContext('request', {
    requestUrl: parsed.requestUrl,
    endpoint: parsed.endpoint,
    httpMethod: parsed.httpMethod,
  });
  scope.setContext('error', {
    error: options.errorBody,
  });
}
